/**
 * RichTextDialog / HtmlPreviewField — corp's rich-text editing for e-forms:
 * the HTML shows in a read-only preview box with a pencil (on hover or focus)
 * that opens a large dialog with corp's editor (RichTextEditor: Visual or
 * Source, Insert Shortcode, Insert Image). Apply hands the HTML back; nothing is saved
 * until the form's own save.
 *
 * RichDescriptionField is the same for the description of a membership, package,
 * product, product type or appointment type, as corp's rich-text-field.tsx does it.
 */
import { useMemo, useState, type ReactNode } from 'react';
import DOMPurify from 'dompurify';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { Modal } from '@/components/ui/Modal';
import { hasRichTextContent } from '@/lib/alertHeroStyle';
import { cn } from '@/lib/utils';
import { RichTextEditor } from './RichTextEditor';
import { fieldClasses } from './DialogChrome';

export function RichTextDialog({
  title,
  value,
  onApply,
  onClose,
  shortcodes = true,
}: {
  title: string;
  value: string;
  onApply: (html: string) => void;
  onClose: () => void;
  shortcodes?: boolean;
}) {
  const [html, setHtml] = useState(value);
  return (
    <Modal
      isOpen
      onClose={onClose}
      closeOnEscape={false}
      size="xxl"
      title={<h2 className="text-base font-semibold text-slate-900">{title}</h2>}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              onApply(html);
              onClose();
            }}
          >
            Apply
          </Button>
        </>
      }
    >
      <RichTextEditor value={html} onChange={setHtml} shortcodes={shortcodes} />
    </Modal>
  );
}

const HTML_TAG_RE = /<\/?[a-z][^>]*>/i;

/**
 * Descriptions saved before the editor existed are plain text. Shown as HTML
 * their line breaks would collapse, so plain text is converted for the preview
 * and the editor. HTML comes back unchanged.
 */
export function descriptionToHtml(value: string): string {
  if (HTML_TAG_RE.test(value)) return value;
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\r?\n/g, '<br>');
}

/**
 * A short preview of a rich-text description. Clicking it (or its pencil)
 * opens the editor; Apply hands the HTML back only when it changed, so an
 * untouched plain-text description stays plain text. Empty editor content
 * comes back as ''. The preview and the editor get the HTML sanitized:
 * descriptions used to be shown only as text, and legacy pages write the same
 * columns.
 */
export function RichDescriptionField({
  id,
  value,
  onChange,
  dialogTitle,
}: {
  id?: string;
  value: string;
  onChange: (html: string) => void;
  dialogTitle: string;
}) {
  const [open, setOpen] = useState(false);
  const locked = useFieldLocked();
  const html = descriptionToHtml(value);
  const safeHtml = useMemo(() => DOMPurify.sanitize(html), [html]);
  return (
    <div className="group relative">
      <div
        id={id}
        role={locked ? undefined : 'button'}
        tabIndex={locked ? undefined : 0}
        aria-label={locked ? undefined : dialogTitle}
        onClick={locked ? undefined : () => setOpen(true)}
        onKeyDown={
          locked
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpen(true);
                }
              }
        }
        className={cn(
          'max-h-[120px] min-h-[42px] overflow-y-auto break-words rounded-md border border-slate-200 bg-white px-3 py-2 pr-10 text-sm text-slate-700',
          '[&_a]:pointer-events-none [&_img]:max-w-full [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_ul]:list-disc [&_ul]:pl-5',
          !locked && 'cursor-pointer hover:border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
        )}
      >
        {hasRichTextContent(safeHtml) ? (
          <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
        ) : (
          <p className="italic text-slate-400">No description</p>
        )}
      </div>
      {!locked && (
        <button
          type="button"
          tabIndex={-1}
          title={dialogTitle}
          aria-hidden
          onClick={() => setOpen(true)}
          className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-800 text-white opacity-0 shadow transition-opacity hover:bg-slate-700 group-hover:opacity-100"
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      {open && !locked && (
        <RichTextDialog
          title={dialogTitle}
          value={safeHtml}
          shortcodes={false}
          onApply={(next) => {
            const v = hasRichTextContent(next) ? next : '';
            if (v !== safeHtml && v !== html && v !== value) onChange(v);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

export function HtmlPreviewField({
  html,
  empty,
  editLabel,
  dialogTitle,
  onChange,
  invalid = false,
  maxHeightClass = 'max-h-[200px]',
  children,
}: {
  html: string;
  /** Shown when there is nothing, e.g. "No header message". */
  empty: string;
  /** The pencil's name, e.g. "Edit header message". */
  editLabel: string;
  dialogTitle: string;
  onChange: (html: string) => void;
  invalid?: boolean;
  maxHeightClass?: string;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  // In a locked FieldLockScope the HTML shows to read, with no pencil (plan 2026-10-10-001, R18).
  const locked = useFieldLocked();
  return (
    <div className="group relative">
      <div
        className={cn(
          'min-h-[60px] overflow-y-auto rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700',
          '[&_img]:max-w-full [&_p]:my-1',
          maxHeightClass,
          invalid && fieldClasses.input('error'),
        )}
      >
        {html.trim() ? (
          // The account's own content, as corp shows it.
          <div dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <p className="italic text-slate-400">{empty}</p>
        )}
        {children}
      </div>
      {!locked && (
        <button
          type="button"
          title={editLabel}
          aria-label={editLabel}
          onClick={() => setOpen(true)}
          className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-slate-800 text-white opacity-0 shadow transition-opacity hover:bg-slate-700 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 group-hover:opacity-100"
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
      {open && !locked && <RichTextDialog title={dialogTitle} value={html} onApply={onChange} onClose={() => setOpen(false)} />}
    </div>
  );
}
